import { Button, chakra, Heading, Input, VStack, Box, useToast } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../app/typedReduxHooks'
import { RootState } from '../../../app/store'
import { STATUS } from '../../../constants'
import InputField from '../../../components/InputField'
import { updateOrganizationData } from '../OrganizationSlice'
import DetailDisplay from './DetailDisplay'

const FormWithStyle = chakra('form')

const clinicDetailsToDisplay = {
  name: 'Name',
  email: 'Email',
  hpio_number: 'HPI-O Number',
  address_1: 'Address Line 1',
  address_2: 'Address Line 2',
  suburb: 'Suburb',
  postcode: 'Postal Code',
  state: 'State',
}

const OrganizationDetails = ({ isAbleToManageOrg }: { isAbleToManageOrg?: boolean }) => {
  const dispatch = useAppDispatch()
  const organization = useSelector((state: RootState) => state.organization?.data)
  const toast = useToast()
  const { updateOrganizationProfile } = useSelector(
    (state: RootState) => state.organization?.status
  )
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm()

  if (!organization) {
    return null
  }

  const handleUpdate = async (value: any) => {
    try {
      if (organization.id) {
        await dispatch(updateOrganizationData({ id: organization.id, ...value })).unwrap()
        toast({
          title: 'Organization profile updated successfully',
          status: 'success',
          duration: 3000,
          position: 'top',
        })
      }
    } catch (error) {
      toast({
        title: 'Failed to update organization profile',
        status: 'error',
        duration: 3000,
        position: 'top',
      })
    }
  }

  return (
    <Box flex={1}>
      <Heading as="h3" size="md" mb={4} color="gray.800" fontWeight="bold">
        Organisation Profile
      </Heading>

      <FormWithStyle onSubmit={handleSubmit(handleUpdate)} w="100%" maxW="400px" mb={8}>
        <VStack spacing={4} align="flex-start">
          <InputField label="Name" field="name" errors={errors}>
            <Input
              disabled={!isAbleToManageOrg}
              placeholder="Name"
              bg="white"
              {...register('name', { value: organization.name || '' })}
            />
          </InputField>
          {isAbleToManageOrg && (
            <Button
              colorScheme="blue"
              size="md"
              type="submit"
              px={8}
              isLoading={updateOrganizationProfile === STATUS.PENDING}
              loadingText="Submitting"
            >
              Submit
            </Button>
          )}
        </VStack>
      </FormWithStyle>

      <Heading as="h3" size="md" mb={4} color="gray.800" fontWeight="bold">
        Clinic Details
      </Heading>
      {organization.clinic && (
        <VStack align="stretch" spacing={3}>
          {Object.entries(clinicDetailsToDisplay).map(([fieldKey, fieldLabel]) => {
            if (!organization.clinic) {
              return null
            }
            const value = organization.clinic[fieldKey as keyof typeof organization.clinic]
            if (value === null || value === undefined) {
              return null
            }
            return <DetailDisplay key={fieldKey} label={fieldLabel} value={String(value ?? '')} />
          })}
        </VStack>
      )}
    </Box>
  )
}

export default OrganizationDetails
